import {
    Box,
    Card,
    Typography,
    Divider,
    Button,
    Grid,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Link,
} from "@mui/material";
import { useState } from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import COLORS from "../../../../../constants/colors";
import baseURL from "../../../user/config";
import { toast } from "react-toastify";

import ConfirmationDialog from '../../ConfirmationDialog';


const NationalIDUpload = ({ userRole, nationalIdData, onDataUpdated }) => {
    const [frontFile, setFrontFile] = useState(null);
    const [backFile, setBackFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [isDeleteAccountDialogOpen, setIsDeleteAccountDialogOpen] = useState(false);
    const [deleteAccountPassword, setDeleteAccountPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    
    // Check if files have been uploaded based on the prop from the parent
    const hasUploaded = !!nationalIdData?.nationalIdFrontUrl;

    const handleFileChange = (event, side) => {
        const file = event.target.files[0];
        if (file) {
            if (side === "front") {
                setFrontFile(file);
            } else {
                setBackFile(file);
            }
        }
    };

    const handleRemoveFile = (side) => {
        if (side === "front") {
            setFrontFile(null);
        } else {
            setBackFile(null);
        }
    };

    const handleUploadSubmit = async () => {
        if (!frontFile || !backFile) {
            toast.error("Please upload both the front and back of your National ID.");
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("nationalIdFront", frontFile);
        formData.append("nationalIdBack", backFile);

        try {
            const response = await fetch(`${baseURL}/org/upload-id`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to upload National ID.');
            }

            console.log("National ID uploaded successfully:", data.data);

            setFrontFile(null);
            setBackFile(null);
            setLoading(false);
            setIsSuccessDialogOpen(true);
            onDataUpdated(); // Notify the parent to re-fetch all data
        } catch (error) {
            console.error("Error uploading national ID:", error);
            setLoading(false);
            toast.error(`Error: ${error.message}`);
        }
    };

    const handleDeleteAccount = async () => {
        setSubmitting(true);
        try {
            const response = await fetch(`${baseURL}/org/user`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ password: deleteAccountPassword }),
                credentials: 'include',
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || 'Failed to delete account.');
            }

            setIsDeleteAccountDialogOpen(false);
            toast.success(result.message);
            window.location.href = "/user/login";
        } catch (error) {
            console.error('Account deletion error:', error);
            toast.error(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const UploadBox = ({ label, side, file, previewUrl, onFileChange, onRemoveFile }) => {
        const showRemoveButton = !!file;

        return (
            <Box
                sx={{
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    cursor: previewUrl || hasUploaded ? "default" : "pointer",
                    position: "relative",
                    minHeight: "150px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                        borderColor: previewUrl || hasUploaded ? "#ccc" : COLORS.primary,
                        bgcolor: previewUrl || hasUploaded ? "transparent" : "#f5f7fa",
                    },
                }}
                component="label"
            >
                {previewUrl || hasUploaded ? (
                    <Box sx={{ position: "relative", width: "100%", height: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img
                            src={previewUrl || nationalIdData[side === 'front' ? 'nationalIdFrontUrl' : 'nationalIdBackUrl']}
                            alt={`${label} Preview`}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "150px",
                                borderRadius: "8px",
                                objectFit: "contain",
                            }}
                        />
                         {hasUploaded && (
                            <Link href={nationalIdData[side === 'front' ? 'nationalIdFrontUrl' : 'nationalIdBackUrl']} target="_blank" rel="noopener" sx={{ mt: 1 }}>View Document</Link>
                         )}
                        {showRemoveButton && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveFile(side);
                                }}
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    bgcolor: "rgba(255, 255, 255, 0.7)",
                                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.9)" },
                                }}
                            >
                                <CancelOutlinedIcon color="error" />
                            </IconButton>
                        )}
                    </Box>
                ) : (
                    <>
                        <CloudUploadOutlinedIcon sx={{ fontSize: "3rem", color: "#9e9e9e" }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {label}
                        </Typography>
                    </>
                )}
                <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => onFileChange(e, side)}
                    disabled={hasUploaded}
                />
            </Box>
        );
    };

    if (userRole !== 'Admin') {
        return null; // Don't render anything if the user is not an Admin
    }

    return (
        <Box sx={{ mt: 3, mx: "auto" }}>
            <Card
                sx={{
                    backgroundColor: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    p: 3,
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AddPhotoAlternateOutlinedIcon sx={{ fontSize: "1.6rem", mr: 1.5, color: "#2D4A3D" }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "1.05rem", letterSpacing: "-0.3px" }}>
                        National ID Verification
                    </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    To complete your account verification, please upload clear images of both the
                    front and back of your national ID. The images must be in a `.jpg`, `.png`, or
                    `.pdf` format and not exceed 5MB each.
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <UploadBox
                            label="Front of National ID"
                            side="front"
                            file={frontFile}
                            previewUrl={frontFile ? URL.createObjectURL(frontFile) : null}
                            onFileChange={handleFileChange}
                            onRemoveFile={handleRemoveFile}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <UploadBox
                            label="Back of National ID"
                            side="back"
                            file={backFile}
                            previewUrl={backFile ? URL.createObjectURL(backFile) : null}
                            onFileChange={handleFileChange}
                            onRemoveFile={handleRemoveFile}
                        />
                    </Grid>
                </Grid>

                {hasUploaded ? (
                    <Button variant="contained" disabled fullWidth sx={{ mt: 3, textTransform: "none", backgroundColor: '#2D4A3D' }}>
                        National ID Uploaded
                    </Button>
                ) : (
                    <Button variant="contained" startIcon={<CloudUploadOutlinedIcon />} onClick={handleUploadSubmit} disabled={loading || !frontFile || !backFile} fullWidth sx={{ mt: 3, textTransform: "none", backgroundColor: '#2D4A3D', '&:hover': { backgroundColor: '#2D4A3D', opacity: 0.9 }, boxShadow: 0 }}>
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Upload National ID"}
                    </Button>
                )}
            </Card>

            <Card
                sx={{
                    backgroundColor: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    mt: 3,
                    p: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DeleteForeverOutlinedIcon color="error" sx={{ fontSize: '1.6rem', mr: 1.5 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "1.05rem", letterSpacing: "-0.3px" }}>
                        Delete Account
                    </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Permanently delete your account and all associated data. This action cannot be undone.
                </Typography>
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForeverOutlinedIcon />}
                    onClick={() => setIsDeleteAccountDialogOpen(true)}
                    sx={{ textTransform: 'none' }}
                >
                    Delete Account
                </Button>
            </Card>

            <Dialog open={isSuccessDialogOpen} onClose={() => setIsSuccessDialogOpen(false)}>
                <DialogTitle sx={{ textAlign: 'center' }}>Upload Successful! 🎉</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" align="center">
                        Your national ID has been successfully uploaded for verification.
                        We will review it shortly.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button onClick={() => setIsSuccessDialogOpen(false)} variant="contained" autoFocus sx={{ backgroundColor: '#2D4A3D', '&:hover': { backgroundColor: '#2D4A3D', opacity: 0.9 }, boxShadow: 0, textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                open={isDeleteAccountDialogOpen}
                onClose={() => setIsDeleteAccountDialogOpen(false)}
                onConfirm={handleDeleteAccount}
                title="Are you sure you want to delete your account?"
                message={
                    <>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            This action is <strong>Permanent</strong> and cannot be undone. All your data, including profile
                            information and transactions, will be lost.
                        </Typography>
                        <TextField
                            label="Enter your password to confirm"
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={deleteAccountPassword}
                            onChange={(e) => setDeleteAccountPassword(e.target.value)}
                            disabled={submitting}
                            sx={{ mt: 2 }}
                        />
                    </>
                }
                confirmText={
                    submitting ? (
                        <>
                            <CircularProgress size={18} sx={{ mr: 1 }} /> Deleting...
                        </>
                    ) : (
                        'Delete Account'
                    )
                }
                cancelText="Cancel"
            />
        </Box>
    );
};

export default NationalIDUpload;